import { useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./lib/supabase";
import { api } from "./lib/api";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import OnboardingPage from "./pages/OnboardingPage";

type AuthState = "loading" | "signed_out" | "needs_onboarding" | "ready";

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [member, setMember] = useState<any>(null);
  const [state, setState] = useState<AuthState>("loading");

  const loadMember = async (s: Session) => {
    try {
      const { member } = await api.me();
      setMember(member);
      const onboardingComplete = member?.preferences?.onboarding_complete === true;
      setState(onboardingComplete ? "ready" : "needs_onboarding");
    } catch (e) {
      // If no member row exists yet (rare race with the auth trigger), retry once.
      setTimeout(() => {
        api.me()
          .then(({ member }) => {
            setMember(member);
            const ok = member?.preferences?.onboarding_complete === true;
            setState(ok ? "ready" : "needs_onboarding");
          })
          .catch(() => setState("needs_onboarding"));
      }, 1500);
    }
  };

  useEffect(() => {
    // Single source of truth: onAuthStateChange. Supabase guarantees it fires an
    // INITIAL_SESSION event on subscribe with the current session (or null), and
    // a TOKEN_REFRESHED event if the access_token is auto-refreshed from storage.
    // Calling getSession() alongside introduces a race where a stale `null`
    // returns before the client finishes hydrating from localStorage — which
    // landed the user on LoginPage after an OAuth bounce. See engagement log.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      if (s) {
        loadMember(s);
      } else if (event === "INITIAL_SESSION" || event === "SIGNED_OUT") {
        setState("signed_out");
      }
      // Other events (TOKEN_REFRESHED, USER_UPDATED) keep current state until
      // their session resolves; they don't kick us to LoginPage on transient null.
    });

    return () => subscription.unsubscribe();
  }, []);

  if (state === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-textBody text-sm tracking-wider uppercase">Loading…</div>
      </div>
    );
  }

  if (state === "signed_out") return <LoginPage />;

  if (state === "needs_onboarding" && session && member) {
    return (
      <OnboardingPage
        session={session}
        member={member}
        onComplete={() => loadMember(session)}
      />
    );
  }

  if (state === "ready" && session) return <DashboardPage session={session} />;

  // Fallback — shouldn't trigger; included for completeness
  return <LoginPage />;
}
